import React from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Spin, Checkbox } from "antd";
import { l } from "../../libs/localization";
import DashboardCompanyColumn from "./components/DashboardCompanyColumn";
import DashboardComparisonGraphs from "./components/DashboardComparisonGraphs";
import DashboardEmptyState from "./components/DashboardEmptyState";
import AddCompanyModal from "./components/AddCompanyModal";
import BrandAdvertisersTable from "../../components/BrandAdvertisers/BrandAdvertisersTable";
import DashboardPitchList from "./components/DashboardPitchList";
import useMultipleCompanyDetails from "../../hooks/useMultipleCompanyDetails";
import useTreeData from "../../hooks/useTreeData";
import { useHistory } from "react-router-dom";
import { Button, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import "./dashboard.scss";

const DashboardPage = () => {
  const params = useParams();

  // Extract up to 5 IDs from params
  const ids = [
    params.id1,
    params.id2,
    params.id3,
    params.id4,
    params.id5,
  ].filter((id) => !!id && !id.startsWith(":"));

  const { companies, loading: companiesLoading } =
    useMultipleCompanyDetails(ids);
  const { data: sector, loading: sectorLoading } = useTreeData("Sector");

  const loading = companiesLoading || sectorLoading;
  const history = useHistory();
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [selectedYear, setSelectedYear] = React.useState(
    new Date().getFullYear() - 1,
  );
  const [socialFilters, setSocialFilters] = React.useState({
    companyIds: ids,
    page: 0,
    pageSize: 10,
  });

  const [selectedSocialCompanyIds, setSelectedSocialCompanyIds] =
    React.useState(ids);

  const [pitchFilters, setPitchFilters] = React.useState({
    companyIds: ids,
    page: 1,
    limit: 10,
  });

  const [selectedPitchCompanyIds, setSelectedPitchCompanyIds] =
    React.useState(ids);

  const [agenciesExpanded, setAgenciesExpanded] = React.useState(false);

  React.useEffect(() => {
    // Sync selection when dashboard companies change
    setSelectedSocialCompanyIds((prev) => {
      const filtered = prev.filter((id) => ids.includes(id)); // Keep only still-present ones
      const newOnes = ids.filter((id) => !prev.includes(id)); // Add newly added ones
      return [...filtered, ...newOnes];
    });
    setSelectedPitchCompanyIds((prev) => {
      const filtered = prev.filter((id) => ids.includes(id));
      const newOnes = ids.filter((id) => !prev.includes(id));
      return [...filtered, ...newOnes];
    });
  }, [JSON.stringify(ids)]);

  React.useEffect(() => {
    setSocialFilters((prev) => ({
      ...prev,
      companyIds: selectedSocialCompanyIds,
      page: 0, // Reset page when base filters change
    }));
  }, [selectedSocialCompanyIds]);
  React.useEffect(() => {
    setPitchFilters((prev) => ({
      ...prev,
      companyIds: selectedPitchCompanyIds,
      page: 1,
    }));
  }, [selectedPitchCompanyIds]);

  const availableYears = React.useMemo(() => {
    const years = new Set();
    companies.forEach((company) => {
      if (company.data?.financial_history) {
        Object.keys(company.data.financial_history).forEach((y) =>
          years.add(y),
        );
      }
      if (company.nielsen?.data?.history) {
        Object.keys(company.nielsen.data.history).forEach((y) => years.add(y));
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [companies]);

  React.useEffect(() => {
    if (
      availableYears.length > 0 &&
      !availableYears.includes(selectedYear.toString())
    ) {
      setSelectedYear(parseInt(availableYears[0]));
    }
  }, [availableYears]);

  const handleAddCompany = (compId) => {
    let newIds;
    if (Array.isArray(compId)) {
      newIds = compId.slice(0, 5);
    } else {
      if (ids.includes(compId)) return;
      newIds = [...ids, compId].slice(0, 5);
    }
    const path = `/dashboard/${newIds.join("/")}`;
    history.push(path);
  };

  const handleRemoveCompany = (compId) => {
    const newIds = ids.filter((id) => id !== compId);
    const path =
      newIds.length > 0 ? `/dashboard/${newIds.join("/")}` : "/dashboard";
    history.push(path);
  };

  return (
    <div className="dashboardPage">
      <Helmet defer={false}>
        <title>{l("PAGES.DASHBOARD.TITLE")} - Blinko</title>
      </Helmet>
      <div
        className="dashboardPage__container"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          padding: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            maxWidth: "1200px",
            margin: "0 auto",
            marginBottom: "20px",
          }}
        >
          <h1 style={{ margin: 0 }}>{l("PAGES.DASHBOARD.TITLE")}</h1>
          {ids.length > 0 && ids.length < 5 && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalVisible(true)}
            >
              {l("COMMON.ADD")}
            </Button>
          )}
        </div>
        {ids.length > 0 ? (
          <div className="dashboardPage__scroll-container">
            {loading ? (
              <div style={{ padding: "100px", textAlign: "center" }}>
                <Spin size="large" />
              </div>
            ) : (
              <div className="dashboardPage__content-wrapper">
                {/* 1. Header Row */}
                <div className="dashboardPage__row">
                  {ids.map((id, index) => {
                    const company = companies.find((c) => c.id === id);
                    return (
                      <DashboardCompanyColumn
                        key={`header-${id}`}
                        id={id}
                        data={company?.data}
                        schema={company?.schema}
                        loading={false}
                        sector={sector}
                        onRemove={handleRemoveCompany}
                        section="header"
                      />
                    );
                  })}
                </div>

                {/* 2. Overview Section */}
                <div className="dashboardPage__section-wrapper">
                  <div className="dashboardPage__section-title">
                    <h2>
                      {l("PAGES.COMPANIES.DETAIL.SECTIONS.GENERAL.OVERVIEW")}
                    </h2>
                  </div>
                  <div className="dashboardPage__row">
                    {ids.map((id, index) => {
                      const company = companies.find((c) => c.id === id);
                      return (
                        <DashboardCompanyColumn
                          key={`overview-${id}`}
                          id={id}
                          data={company?.data}
                          schema={company?.schema}
                          loading={false}
                          sector={sector}
                          section="overview"
                        />
                      );
                    })}
                  </div>
                </div>

                {/* 3. Financials Section */}
                <div className="dashboardPage__section-wrapper">
                  <div className="dashboardPage__section-title">
                    <h2>{l("PAGES.COMPANIES.DETAIL.SECTIONS.FINANCIALS")}</h2>
                  </div>

                  <div className="dashboardPage__common-section">
                    <DashboardComparisonGraphs companies={companies} />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <div className="dashboardPage__row">
                      {ids.map((id, index) => {
                        const company = companies.find((c) => c.id === id);
                        return (
                          <DashboardCompanyColumn
                            key={`mini-financy-${id}`}
                            id={id}
                            data={company?.data}
                            section="mini-header"
                          />
                        );
                      })}
                    </div>

                    <div className="dashboardPage__row">
                      {ids.map((id, index) => {
                        const company = companies.find((c) => c.id === id);
                        return (
                          <DashboardCompanyColumn
                            key={`financials-${id}`}
                            id={id}
                            data={company?.data}
                            schema={company?.schema}
                            loading={false}
                            sector={sector}
                            section="financials"
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* 4. Nielsen Section (Traditional Only) */}
                <div className="dashboardPage__section-wrapper">
                  <div className="dashboardPage__section-title">
                    <h2>{l("PAGES.COMPANIES.DETAIL.SECTIONS.INVESTMENTS")}</h2>
                  </div>

                  {/* Contextual Year Picker for Nielsen */}
                  <div
                    className="dashboardPage__common-section"
                    style={{ padding: "12px 20px", width: "max-content" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: "bold",
                          color: "#666",
                        }}
                      >
                        {l("COMMON.YEAR")}:
                      </span>
                      {availableYears.map((year) => (
                        <Button
                          key={year}
                          size="small"
                          type={
                            selectedYear.toString() === year
                              ? "primary"
                              : "default"
                          }
                          onClick={() => setSelectedYear(parseInt(year))}
                          style={{ borderRadius: "4px" }}
                        >
                          {year}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <div className="dashboardPage__row">
                      {ids.map((id, index) => {
                        const company = companies.find((c) => c.id === id);
                        return (
                          <DashboardCompanyColumn
                            key={`mini-nielsen-${id}`}
                            id={id}
                            data={company?.data}
                            section="mini-header"
                          />
                        );
                      })}
                    </div>
                    <div className="dashboardPage__row">
                      {ids.map((id, index) => {
                        const company = companies.find((c) => c.id === id);
                        return (
                          <DashboardCompanyColumn
                            key={`nielsen-${id}`}
                            id={id}
                            data={company?.data}
                            schema={company?.schema}
                            loading={false}
                            sector={sector}
                            section="nielsen"
                            selectedYear={selectedYear}
                            nielsen={company?.nielsen}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="dashboardPage__section-wrapper">
                  <div className="dashboardPage__section-title">
                    <h2>
                      {l("PAGES.COMPANIES.DETAIL.SECTIONS.DIGITAL_INVESTMENTS")}
                    </h2>
                  </div>

                  {/* Company Toggler for Social Ads */}
                  <div
                    className="dashboardPage__common-section"
                    style={{ padding: "12px 20px", marginBottom: "10px" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "15px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: "bold",
                          color: "#666",
                        }}
                      >
                        {l("COMMON.COMPANIES")}:
                      </span>
                      {companies.map((c) => (
                        <Checkbox
                          key={c.id}
                          checked={selectedSocialCompanyIds.includes(c.id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSelectedSocialCompanyIds((prev) =>
                              checked
                                ? [...prev, c.id]
                                : prev.filter((id) => id !== c.id),
                            );
                          }}
                        >
                          {c.data?.name}
                        </Checkbox>
                      ))}
                    </div>
                  </div>

                  <div
                    className="dashboardPage__common-section"
                    style={{ padding: "20px" }}
                  >
                    <BrandAdvertisersTable
                      filters={socialFilters}
                      onFiltersChange={setSocialFilters}
                    />
                  </div>
                </div>

                {/* 5. Agencies Section */}
                <div className="dashboardPage__section-wrapper">
                  <div
                    className="dashboardPage__section-title"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <h2>{l("PAGES.COMPANIES.DETAIL.SECTIONS.AGENCIES")}</h2>
                    <Button
                      type="link"
                      onClick={() => setAgenciesExpanded(!agenciesExpanded)}
                      style={{ padding: 0, fontWeight: "600" }}
                    >
                      {agenciesExpanded
                        ? l("COMMON.SHOW_LESS")
                        : l("COMMON.SHOW_MORE")}
                    </Button>
                  </div>
                  <div className="dashboardPage__scroll-container">
                    <div
                      className="dashboardPage__row"
                      style={{ marginBottom: "10px" }}
                    >
                      {ids.map((id) => {
                        const company = companies.find((c) => c.id === id);
                        return (
                          <DashboardCompanyColumn
                            key={`mini-agencies-${id}`}
                            id={id}
                            data={company?.data}
                            section="mini-header"
                          />
                        );
                      })}
                    </div>
                    <div className="dashboardPage__row">
                      {ids.map((id) => {
                        const company = companies.find((c) => c.id === id);
                        return (
                          <DashboardCompanyColumn
                            key={`agencies-${id}`}
                            id={id}
                            data={company?.data}
                            schema={company?.schema}
                            loading={false}
                            sector={sector}
                            section="agencies"
                            agencies={company?.agencies}
                            isExpanded={agenciesExpanded}
                            onToggleExpand={() =>
                              setAgenciesExpanded(!agenciesExpanded)
                            }
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* 6. Pitches Section */}
                <div className="dashboardPage__section-wrapper">
                  <div className="dashboardPage__section-title">
                    <h2>{l("PAGES.PITCH.TITLE")}</h2>
                  </div>

                  {/* Company Toggler for Pitches */}
                  <div
                    className="dashboardPage__common-section"
                    style={{ padding: "12px 20px", marginBottom: "10px" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "15px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: "bold",
                          color: "#666",
                        }}
                      >
                        {l("COMMON.COMPANIES")}:
                      </span>
                      {companies.map((c) => (
                        <Checkbox
                          key={c.id}
                          checked={selectedPitchCompanyIds.includes(c.id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSelectedPitchCompanyIds((prev) =>
                              checked
                                ? [...prev, c.id]
                                : prev.filter((id) => id !== c.id),
                            );
                          }}
                        >
                          {c.data?.name}
                        </Checkbox>
                      ))}
                    </div>
                  </div>

                  <div
                    className="dashboardPage__common-section"
                    style={{ padding: "20px" }}
                  >
                    <DashboardPitchList
                      filters={pitchFilters}
                      onFiltersChange={setPitchFilters}
                    />
                  </div>
                </div>

                {/* 7. Creativity Section */}
                <div className="dashboardPage__section-wrapper">
                  <div className="dashboardPage__section-title">
                    <h2>{l("PAGES.COMPANIES.DETAIL.SECTIONS.CREATIVITY")}</h2>
                  </div>
                  <div className="dashboardPage__scroll-container">
                    <div
                      className="dashboardPage__row"
                      style={{ marginBottom: "10px" }}
                    >
                      {ids.map((id) => {
                        const company = companies.find((c) => c.id === id);
                        return (
                          <DashboardCompanyColumn
                            key={`mini-crea-${id}`}
                            id={id}
                            data={company?.data}
                            section="mini-header"
                          />
                        );
                      })}
                    </div>
                    <div className="dashboardPage__row">
                      {ids.map((id) => {
                        const company = companies.find((c) => c.id === id);
                        return (
                          <DashboardCompanyColumn
                            key={`crea-${id}`}
                            id={id}
                            data={company?.data}
                            creas={company?.creas}
                            section="creativity"
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* 8. Mergers and Acquisitions Section */}
                <div className="dashboardPage__section-wrapper">
                  <div className="dashboardPage__section-title">
                    <h2>
                      {l("PAGES.COMPANIES.DETAIL.SECTIONS.ENTITY_NEWS.TITLE")}
                    </h2>
                  </div>
                  <div className="dashboardPage__scroll-container">
                    <div
                      className="dashboardPage__row"
                      style={{ marginBottom: "10px" }}
                    >
                      {ids.map((id) => {
                        const company = companies.find((c) => c.id === id);
                        return (
                          <DashboardCompanyColumn
                            key={`mini-acq-${id}`}
                            id={id}
                            data={company?.data}
                            section="mini-header"
                          />
                        );
                      })}
                    </div>
                    <div className="dashboardPage__row">
                      {ids.map((id) => {
                        const company = companies.find((c) => c.id === id);
                        return (
                          <DashboardCompanyColumn
                            key={`acq-${id}`}
                            id={id}
                            data={company?.data}
                            entityNews={company?.entityNews}
                            section="acquisitions"
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <DashboardEmptyState onAddCompany={() => setIsModalVisible(true)} />
        )}
        <AddCompanyModal
          visible={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          onSelect={handleAddCompany}
          excludeIds={ids}
        />

        {ids.length > 0 && ids.length < 5 && (
          <Tooltip title={l("COMMON.ADD")}>
            <Button
              type="primary"
              shape="circle"
              icon={<PlusOutlined style={{ fontSize: "24px" }} />}
              size="large"
              onClick={() => setIsModalVisible(true)}
              style={{
                position: "fixed",
                bottom: "40px",
                right: "40px",
                width: "60px",
                height: "60px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #FF9B00 0%, #FF6B00 100%)",
                border: "none",
              }}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
